import { z } from 'zod';

import {
  INVESTMENT_ALLOCATION_STATUSES,
  INVESTMENT_THESIS_IDENTITY_SOURCES,
  INVESTMENT_THESIS_STATUSES,
} from '../enums';
import { AccountIdSchema } from './account';

const NullableDecimalStringSchema = z.string().nullable();

export const InvestmentAllocationQuerySchema = z.object({ accountId: AccountIdSchema });

export const InvestmentValuationUnavailableReasonSchema = z.enum([
  'UNSUPPORTED_SOURCE',
  'NO_HOLDINGS_SNAPSHOT',
  'CURRENCY_MISMATCH',
  'INCONSISTENT_TOTALS',
  'NON_POSITIVE_ACCOUNT_VALUE',
]);

export const InvestmentAllocationComparisonGapSchema = z.enum([
  'VALUATION_UNAVAILABLE',
  'THESIS_UNMATCHED',
  'THESIS_NOT_ACTIVE',
  'TARGET_UNSET',
]);

export const InvestmentAllocationHoldingSchema = z.object({
  id: z.string().trim().min(1),
  code: z.string(),
  symbol: z.string(),
  stock: z.string(),
  value: NullableDecimalStringSchema,
  cost: NullableDecimalStringSchema.optional(),
  units: NullableDecimalStringSchema.optional(),
  weightPct: NullableDecimalStringSchema,
  thesisMatchStatus: z.enum([
    'MATCHED',
    'THESIS_MISSING',
    'IDENTITY_AMBIGUOUS',
    'IDENTITY_REVIEW_REQUIRED',
  ]),
  thesis: z
    .object({
      id: z.string().trim().min(1),
      status: z.enum(INVESTMENT_THESIS_STATUSES),
      nextReviewAt: z.string().date().nullable().optional(),
    })
    .nullable(),
  targetPct: NullableDecimalStringSchema,
  maximumPct: NullableDecimalStringSchema,
  differenceFromTargetPct: NullableDecimalStringSchema,
  allocationStatus: z.enum(INVESTMENT_ALLOCATION_STATUSES).nullable(),
  comparisonGap: InvestmentAllocationComparisonGapSchema.nullable(),
});

export const InvestmentAllocationResponseSchema = z.object({
  account: z.object({
    id: z.string().trim().min(1),
    name: z.string(),
    currency: z.string().trim().min(1),
  }),
  valuation: z.object({
    available: z.boolean(),
    unavailableReason: InvestmentValuationUnavailableReasonSchema.nullable(),
    source: z.literal('HL_HOLDINGS_SNAPSHOT').nullable(),
    sourceCurrency: z.string().nullable(),
    /** The UTC instant of the valuation; null when it cannot be recovered exactly. */
    asOf: z.string().datetime().nullable(),
    reportedAsOf: z
      .object({
        localDateTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/),
        timeZone: z.literal('Europe/London'),
        resolution: z.enum(['EXACT', 'AMBIGUOUS', 'NONEXISTENT']),
        utcOffsetMinutes: z.union([z.literal(0), z.literal(60)]).nullable(),
      })
      .nullable(),
    importedAt: z.string().datetime().nullable(),
    stale: z.boolean().nullable(),
    staleAfterDays: z.number().int().positive(),
  }),
  nearTargetTolerancePct: z.string(),
  accountValue: NullableDecimalStringSchema,
  holdingsValue: NullableDecimalStringSchema,
  cash: NullableDecimalStringSchema,
  availableCash: NullableDecimalStringSchema,
  cashWeightPct: NullableDecimalStringSchema,
  holdings: z.array(InvestmentAllocationHoldingSchema),
  activeBelowTargetHoldingIds: z.array(z.string()),
  unmatchedActiveTheses: z.array(
    z.object({
      id: z.string().trim().min(1),
      displaySymbol: z.string(),
      identitySource: z.enum(INVESTMENT_THESIS_IDENTITY_SOURCES),
      identityValue: z.string(),
      targetPct: NullableDecimalStringSchema,
      maximumPct: NullableDecimalStringSchema,
    }),
  ),
});

export type InvestmentAllocationHolding = z.infer<typeof InvestmentAllocationHoldingSchema>;
export type InvestmentAllocationResponse = z.infer<typeof InvestmentAllocationResponseSchema>;
